import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { TenderAnalysisState, AgentFlowStep, FormData } from '../types';

// Define the app state
interface AppState {
  currentStep: 'input' | 'analysis' | 'results';
  isLoading: boolean;
  error: string | null;
  formData: FormData;
  analysisData: TenderAnalysisState | null;
  threadId: string | null;
  agentSteps: AgentFlowStep[];
}

// Define action types
type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_FORM_DATA'; payload: FormData }
  | { type: 'SET_CURRENT_STEP'; payload: 'input' | 'analysis' | 'results' }
  | { type: 'SET_ANALYSIS_DATA'; payload: TenderAnalysisState }
  | { type: 'SET_THREAD_ID'; payload: string }
  | { type: 'UPDATE_AGENT_STEP'; payload: AgentFlowStep }
  | { type: 'ADD_AGENT_STEP'; payload: AgentFlowStep }
  | { type: 'RESET_STATE' };

// Initial state
const initialState: AppState = {
  currentStep: 'input',
  isLoading: false,
  error: null,
  formData: {
    url: '',
    successful_tender_urls: [],
    company_id: '',
  },
  analysisData: null,
  threadId: null,
  agentSteps: [],
};

// Reducer function
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_FORM_DATA':
      return { ...state, formData: action.payload };
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_ANALYSIS_DATA':
      return { ...state, analysisData: action.payload };
    case 'SET_THREAD_ID':
      return { ...state, threadId: action.payload };
    case 'UPDATE_AGENT_STEP':
      return {
        ...state,
        agentSteps: state.agentSteps.map(step =>
          step.id === action.payload.id ? action.payload : step
        ),
      };
    case 'ADD_AGENT_STEP':
      return {
        ...state,
        agentSteps: [...state.agentSteps, action.payload],
      };
    case 'RESET_STATE':
      return initialState;
    default:
      return state;
  }
};

// Context type
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Helper functions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setFormData: (data: FormData) => void;
  setCurrentStep: (step: 'input' | 'analysis' | 'results') => void;
  setAnalysisData: (data: TenderAnalysisState) => void;
  setThreadId: (id: string) => void;
  updateAgentStep: (step: AgentFlowStep) => void;
  addAgentStep: (step: AgentFlowStep) => void;
  resetState: () => void;
}

// Create context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Helper functions
  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  const setFormData = (data: FormData) => {
    dispatch({ type: 'SET_FORM_DATA', payload: data });
  };

  const setCurrentStep = (step: 'input' | 'analysis' | 'results') => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: step });
  };

  const setAnalysisData = (data: TenderAnalysisState) => {
    dispatch({ type: 'SET_ANALYSIS_DATA', payload: data });
  };

  const setThreadId = (id: string) => {
    dispatch({ type: 'SET_THREAD_ID', payload: id });
  };

  const updateAgentStep = (step: AgentFlowStep) => {
    dispatch({ type: 'UPDATE_AGENT_STEP', payload: step });
  };

  const addAgentStep = (step: AgentFlowStep) => {
    dispatch({ type: 'ADD_AGENT_STEP', payload: step });
  };

  const resetState = () => {
    dispatch({ type: 'RESET_STATE' });
  };

  const value: AppContextType = {
    state,
    dispatch,
    setLoading,
    setError,
    setFormData,
    setCurrentStep,
    setAnalysisData,
    setThreadId,
    updateAgentStep,
    addAgentStep,
    resetState,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Custom hook to use the app context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};